import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  type Column,
  useToast,
} from '@/components/ui';
import {
  useStaff,
  usePermissionChangeActions,
  usePermissionChanges,
  usePermissionMatrix,
  type PermissionMatrix,
  type PermissionProposal,
  type StaffRow,
} from '@/features/admin-v2/api';
import { useMe, useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useDoctorProfileAccess } from '@/features/doctor-profiles/access';
import { StaffSessionsDrawer } from '@/features/staff-sessions/StaffSessionsDrawer';
import {
  locumContractErrorKind,
  useLocums,
  type LocumContract,
} from '@/features/locum/api';
import { PageContainer } from '@/layouts/PageContainer';
import { api, isApiError } from '@/lib/api';
import dash from '@/pages/dashboards/dashboards.module.css';
import local from '@/pages/finance/finance.module.css';
import { dayLabel } from '@/pages/ops/register-format';
import { ContractSummary } from '@/pages/locum/MyContractPage';
import { LocumContractEditor } from '@/pages/locum/LocumContractEditor';
import parity from '@/pages/ops/parity.module.css';
import styles from '@/pages/queue/queue.module.css';
import { pinLabel, type BranchInfo } from './branch-details';
import { areaName, permissionWords } from './permission-words';
import { filterStaff, staffRoleName, staffStatusWords } from './staff-roles';
import { DoctorProfileDrawer } from './DoctorProfileDrawer';
import { EditBranchDialog } from './EditBranchDialog';
import { InviteButton, StaffInvitations } from './StaffInvites';
import { ResetStaffMfaDialog } from './ResetStaffMfaDialog';
import { useNavigate } from 'react-router';
import {
  StaffAccessSection,
  StaffPasswordSection,
  StaffRolesSection,
} from './StaffAccessSections';

function useFail() {
  const toast = useToast();
  return (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not saved',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
}

interface Hours {
  branch: { id: string };
  hours: {
    opens?: string;
    closes?: string;
    fridayBreakStart?: string;
    fridayBreakEnd?: string;
  };
}

const notSet = <span className={styles.sub}>Not set</span>;

/** "Branches & rooms" (prototype branches(): one card per clinic, Edit clinic information). */
export function Branches() {
  const { hasPermission } = useSession();
  const branches = useQuery({
    queryKey: ['branches', 'detail'],
    queryFn: ({ signal }) =>
      api.get<BranchInfo[]>('/branches', { signal }).then((r) => r.data),
  });
  const hours = useQuery({
    queryKey: ['calendar', 'hours'],
    queryFn: ({ signal }) =>
      api.get<Hours[]>('/calendar/hours', { signal }).then((r) => r.data),
  });
  const [editing, setEditing] = useState<BranchInfo | null>(null);
  const list = (items: string[]) => (items.length ? items.join(' · ') : notSet);
  return (
    <PageContainer>
      <p className={local.note}>
        What you save here is what patients see in the app: address, phone,
        WhatsApp, map pin, parking, panels, services, opening hours and the
        clinic information screen.
      </p>
      {branches.isLoading ? (
        <LoadingState label="Loading branches" />
      ) : branches.error ? (
        <ErrorState
          error={branches.error}
          onRetry={() => void branches.refetch()}
        />
      ) : !branches.data?.length ? (
        <EmptyState
          title="No active branches"
          body="Active clinic branches appear here."
        />
      ) : (
        <div className={dash.halves}>
          {branches.data.map((b) => {
            const h = hours.data?.find((x) => x.branch.id === b.id)?.hours;
            const address = [
              b.address.line1,
              b.address.line2,
              [b.address.postcode, b.address.city].filter(Boolean).join(' '),
              b.address.state,
            ]
              .filter(Boolean)
              .join(', ');
            const pin = pinLabel(b);
            return (
              <Card
                key={b.id}
                title={`Ommri Klinik ${b.name}`}
                actions={
                  <Badge
                    tone={b.status === 'active' ? 'success' : 'neutral'}
                    dot
                  >
                    {b.status === 'active' ? 'Open' : 'Closed'}
                  </Badge>
                }
              >
                <div className={local.form}>
                  <dl className={local.kv}>
                    <dt>Address</dt>
                    <dd>{address || notSet}</dd>
                    <dt>Phone</dt>
                    <dd>{b.phone ?? notSet}</dd>
                    <dt>WhatsApp</dt>
                    <dd>{b.whatsapp ?? notSet}</dd>
                    <dt>Email</dt>
                    <dd>{b.email ?? notSet}</dd>
                    <dt>Map pin</dt>
                    <dd>{pin ?? notSet}</dd>
                    <dt>Parking</dt>
                    <dd>{b.parkingNote ?? notSet}</dd>
                    <dt>Panels accepted</dt>
                    <dd>{list(b.panelsAccepted)}</dd>
                    <dt>Services</dt>
                    <dd>{list(b.servicesOffered)}</dd>
                    <dt>Rooms</dt>
                    <dd>{list(b.rooms)}</dd>
                    <dt>Facilities</dt>
                    <dd>{list(b.facilities)}</dd>
                    <dt>Hours</dt>
                    <dd>
                      {h?.opens && h.closes
                        ? `${h.opens} to ${h.closes} daily`
                        : notSet}
                    </dd>
                    <dt>MOH reg</dt>
                    <dd>{b.mohRegistrationNo ?? 'To be supplied'}</dd>
                  </dl>
                  {hasPermission('branches:manage') && (
                    <Button
                      block
                      icon={<Pencil size={16} />}
                      onClick={() => setEditing(b)}
                    >
                      Edit clinic information
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      {editing && (
        <EditBranchDialog branch={editing} onClose={() => setEditing(null)} />
      )}
    </PageContainer>
  );
}

interface RosterRow {
  id: string;
  staffUserId: string;
  branchId: string;
  doctor: string | null;
  branch: string;
  weekday: number;
  startMin: number;
  endMin: number;
}
const WEEK = [1, 2, 3, 4, 5, 6, 0];
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const time = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** "Doctors & rosters · this week" (prototype rosters(): one row per doctor, a cell per weekday). */
export function Rosters() {
  const me = useMe();
  const navigate = useNavigate();
  const { hasPermission } = useSession();
  const { selected } = useBranch();
  const q = useQuery({
    queryKey: ['admin', 'rosters', selected?.id ?? 'all'],
    queryFn: ({ signal }) =>
      api.get<RosterRow[]>('/admin/rosters', { signal }).then((r) => r.data),
  });
  // RL-1160: clinic managers reach their doctors' public profiles here.
  const profiles = useDoctorProfileAccess();
  const [profileFor, setProfileFor] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const doctors = [
    ...new Map(
      (q.data ?? []).map((r) => [r.staffUserId, r.doctor ?? 'Doctor']),
    ).entries(),
  ];
  const code = (branchName: string) => initials(branchName);
  const cell = (id: string, day: number) =>
    (q.data ?? [])
      .filter((r) => r.staffUserId === id && r.weekday === day)
      .map(
        (r) =>
          `${me.branches.length > 1 ? `${code(r.branch)} ` : ''}${time(r.startMin)}–${time(r.endMin)}`,
      )
      .join(' · ') || '-';
  const columns: Column<[string, string]>[] = [
    {
      key: 'd',
      header: 'Doctor',
      render: ([, name]) => <strong>{name}</strong>,
    },
    ...WEEK.map((d) => ({
      key: `w${d}`,
      header: DAY[d],
      render: ([id]: [string, string]) => cell(id, d),
    })),
    {
      key: 'a',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right' as const,
      render: ([id, name]: [string, string]) => (
        <span className={local.badges}>
          {profiles.canRead && (
            <Button
              size="sm"
              aria-label={`Public profile of ${name}`}
              onClick={() => setProfileFor({ id, name })}
            >
              Public profile
            </Button>
          )}
        </span>
      ),
    },
  ];
  return (
    <PageContainer>
      <Card
        title="Doctors & rosters · this week"
        flush
        actions={
          // RL-1886: hours are set on the one Weekly hours page.
          hasPermission('schedules:manage') && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => void navigate('/setup/weekly-hours')}
            >
              Edit weekly hours
            </Button>
          )
        }
      >
        <DataTable
          caption="Rosters"
          rows={q.data ? doctors : undefined}
          rowKey={([id]) => id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          columns={columns}
          empty={
            <EmptyState
              title="No weekly hours yet"
              body="Set each doctor's weekly hours on the Weekly hours page."
            />
          }
        />
        <p className={styles.sub} style={{ padding: '12px 20px' }}>
          {me.branches.map((b) => `${code(b.name)} = ${b.name}`).join(' · ')}.
          Weekly hours feed attendance, My schedule, revenue, the app's doctor
          picker and "Any available doctor".
        </p>
      </Card>
      {profileFor && (
        <DoctorProfileDrawer
          staffId={profileFor.id}
          name={profileFor.name}
          onClose={() => setProfileFor(null)}
        />
      )}
    </PageContainer>
  );
}

/** GET /admin/staff returns at most this many rows. */
const STAFF_LIST_CAP = 300;

const ROLE_ORDER = [
  'front_desk',
  'doctor',
  'locum',
  'mental_health',
  'nurse',
  'lab',
  'dispenser',
  'finance',
  'clinic_manager',
  'super_admin',
];

/** "Hakim Aziz · 20 Oct 2026", or a dash for accounts made without an invite. */
function InvitedBy({ staff }: { staff: StaffRow }) {
  if (!staff.invitedAt) return <span className={styles.sub}>-</span>;
  return (
    <>
      {staff.invitedByName ?? 'Unknown'}
      <div className={styles.sub}>{dayLabel(staff.invitedAt)}</div>
    </>
  );
}

/** "Staff & roles" (prototype staff(): roles per branch, 2FA, status, last sign-in). */
export function Staff() {
  const { t } = useTranslation();
  const me = useMe();
  const staff = useStaff();
  const profiles = useDoctorProfileAccess();
  const [profileFor, setProfileFor] = useState<StaffRow | null>(null);
  const [selected, setSelected] = useState<StaffRow | null>(null);
  // RL-1793 (#155): search by name or email, filter by role.
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const shown = filterStaff(staff.data, search, roleFilter);
  const columns: Column<StaffRow>[] = [
    {
      key: 'p',
      header: 'Person',
      render: (s) => (
        <>
          <p className={styles.name}>{s.fullName}</p>
          <p className={styles.sub}>
            {[s.email, s.mmcNumber ? `MMC ${s.mmcNumber}` : '']
              .filter(Boolean)
              .join(' · ')}
          </p>
        </>
      ),
    },
    ...me.branches.map((b) => ({
      key: `b-${b.id}`,
      header: b.name,
      render: (s: StaffRow) => {
        const roles = (s.roles ?? []).filter(
          (r) =>
            r.branch === null || r.branch === b.code || r.branch === b.name,
        );
        return roles.length ? (
          <span className={local.badges}>
            {roles
              .sort(
                (x, y) =>
                  ROLE_ORDER.indexOf(x.role) - ROLE_ORDER.indexOf(y.role),
              )
              .map((r) => (
                <Badge key={r.role} tone="brand">
                  {staffRoleName(r.role)}
                </Badge>
              ))}
          </span>
        ) : (
          '-'
        );
      },
    })),
    {
      key: 'mfa',
      header: '2FA',
      render: (s) => (
        <Badge tone={s.mfaEnabled ? 'success' : 'warning'}>
          {s.mfaEnabled ? 'On' : 'Off'}
        </Badge>
      ),
    },
    {
      key: 'st',
      header: 'Status',
      render: (s) => (
        <Badge
          tone={s.status === 'active' ? 'success' : 'danger'}
          dot={s.status === 'active'}
        >
          {staffStatusWords(s.status)}
        </Badge>
      ),
    },
    {
      key: 'inv',
      header: 'Invited by',
      render: (s) => <InvitedBy staff={s} />,
    },
    {
      key: 'last',
      header: 'Last sign-in',
      render: (s) => (s.lastSignedInAt ? dayLabel(s.lastSignedInAt) : 'Never'),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (s) => (
        <div className={parity.rowActions}>
          <Button size="sm" onClick={() => setSelected(s)}>
            {t('staff.view')}
          </Button>
          {/* RL-1160: the doctor's public app profile (qualifications, photo). */}
          {profiles.canRead && s.roles?.some((r) => r.role === 'doctor') ? (
            <Button
              size="sm"
              aria-label={`Public profile of ${s.fullName}`}
              onClick={() => setProfileFor(s)}
            >
              Public profile
            </Button>
          ) : null}
        </div>
      ),
    },
  ];
  return (
    <PageContainer>
      <Card title="Staff & roles" flush actions={<InviteButton />}>
        <div className={parity.listFilters}>
          <Field label="Search staff">
            <Input
              type="search"
              value={search}
              placeholder="Name or email"
              onChange={(e) => setSearch(e.target.value)}
            />
          </Field>
          <Field label="Role">
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              options={[
                { value: '', label: 'All roles' },
                ...ROLE_ORDER.map((r) => ({
                  value: r,
                  label: staffRoleName(r),
                })),
              ]}
            />
          </Field>
        </div>
        {/* The API returns at most 300 staff (RL-1793 review). */}
        {staff.data?.length === STAFF_LIST_CAP && (
          <p className={local.infoNote} style={{ margin: '12px 20px 0' }}>
            Showing the first {STAFF_LIST_CAP} staff; search covers these only.
          </p>
        )}
        <DataTable
          caption="Staff"
          rows={shown}
          rowKey={(s) => s.id}
          columns={columns}
          loading={staff.isLoading}
          error={staff.error}
          onRetry={() => void staff.refetch()}
          empty={
            <EmptyState
              title={search || roleFilter ? 'No staff match' : 'No staff yet'}
            />
          }
        />
      </Card>
      <StaffInvitations />
      {profileFor && (
        <DoctorProfileDrawer
          staffId={profileFor.id}
          name={profileFor.fullName}
          onClose={() => setProfileFor(null)}
        />
      )}
      {selected && (
        <StaffDrawer staff={selected} onClose={() => setSelected(null)} />
      )}
    </PageContainer>
  );
}

function StaffDrawer({
  staff,
  onClose,
}: {
  staff: StaffRow;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const { hasPermission } = useSession();
  const isLocum = staff.roles?.some((role) => role.role === 'locum') ?? false;
  const contracts = useLocums(isLocum);
  const [editing, setEditing] = useState<LocumContract | 'new' | null>(null);
  const [resettingMfa, setResettingMfa] = useState(false);
  const [viewingSessions, setViewingSessions] = useState(false);
  const staffContracts =
    contracts.data?.filter((contract) => contract.locumUserId === staff.id) ??
    [];

  return (
    <Dialog
      open
      title={staff.fullName}
      onClose={onClose}
      dismissible={editing === null}
      footer={
        editing === null ? (
          <Button onClick={onClose}>{t('common.close')}</Button>
        ) : undefined
      }
    >
      <div className={parity.form}>
        <dl className={parity.kv}>
          <dt>{t('locum.email')}</dt>
          <dd>{staff.email}</dd>
          <dt>{t('staff.status')}</dt>
          <dd>{staffStatusWords(staff.status)}</dd>
          <dt>Invited by</dt>
          <dd>
            <InvitedBy staff={staff} />
          </dd>
          <dt>{t('staff.twoFactor')}</dt>
          <dd>
            {staff.mfaEnabled ? t('staff.enabled') : t('staff.notEnabled')}
          </dd>
        </dl>

        {/* RL-1783/1786/1788: roles, access and password on the staff record. */}
        {hasPermission('staff:manage') && (
          <>
            <StaffRolesSection staff={staff} />
            <StaffAccessSection staff={staff} />
            <StaffPasswordSection staff={staff} />
          </>
        )}

        {hasPermission('sessions:revoke') && (
          <section aria-label={t('staffSessions.signedInDevices')}>
            <div className={parity.split}>
              <span>{t('staffSessions.staffIntro')}</span>
              <Button size="sm" onClick={() => setViewingSessions(true)}>
                {t('staffSessions.viewSessions')}
              </Button>
            </div>
          </section>
        )}

        {me.activeRole.key === 'super_admin' && me.id !== staff.id && (
          <section aria-label={t('staff.resetTwoFactor')}>
            <div className={parity.split}>
              <span>{t('staff.resetTwoFactorBody')}</span>
              <Button
                size="sm"
                variant="danger"
                onClick={() => setResettingMfa(true)}
              >
                {t('staff.resetTwoFactor')}
              </Button>
            </div>
          </section>
        )}

        {isLocum && (
          <section aria-label={t('staff.locumContracts')}>
            <div className={parity.split}>
              <strong>{t('staff.locumContracts')}</strong>
              {hasPermission('locums:manage') && editing === null && (
                <Button size="sm" onClick={() => setEditing('new')}>
                  {t('staff.addContract')}
                </Button>
              )}
            </div>
            {editing !== null ? (
              <LocumContractEditor
                key={editing === 'new' ? 'new' : editing.id}
                locumUserId={staff.id}
                roleBranch={
                  staff.roles?.find(
                    (role) => role.role === 'locum' && role.branch,
                  )?.branch
                }
                contract={editing === 'new' ? null : editing}
                onCancel={() => setEditing(null)}
                onSaved={() => setEditing(null)}
              />
            ) : contracts.isLoading ? (
              <LoadingState />
            ) : contracts.error ? (
              <ErrorState
                error={contracts.error}
                body={
                  locumContractErrorKind(contracts.error) === 'not_found'
                    ? t('locum.errors.notFoundManage')
                    : undefined
                }
                onRetry={() => void contracts.refetch()}
              />
            ) : staffContracts.length ? (
              <div className={parity.form}>
                {staffContracts.map((contract) => (
                  <div key={contract.id} className={parity.form}>
                    <ContractSummary contract={contract} />
                    {hasPermission('locums:manage') && (
                      <Button size="sm" onClick={() => setEditing(contract)}>
                        {t('staff.editContract')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                title={t('locum.noContract')}
                body={t('staff.noContractBody')}
              />
            )}
          </section>
        )}
      </div>
      {resettingMfa && (
        <ResetStaffMfaDialog
          staffId={staff.id}
          staffName={staff.fullName}
          onClose={() => setResettingMfa(false)}
        />
      )}
      {viewingSessions && (
        <StaffSessionsDrawer
          staffId={staff.id}
          staffName={staff.fullName}
          onClose={() => setViewingSessions(false)}
        />
      )}
    </Dialog>
  );
}

type MatrixRole = PermissionMatrix['roles'][number];
type ProposalDraft = {
  role: MatrixRole;
  permission: string;
  change: 'grant' | 'revoke';
};
/** `self`: RL-2019, the lone Super admin decides their own proposal. */
type Decision = {
  proposal: PermissionProposal;
  action: 'approve' | 'reject';
  self?: boolean;
};

const label = (key: string) =>
  key.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase());
const when = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-MY', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '-';
const changeBadge = (change: 'grant' | 'revoke') =>
  change === 'grant' ? (
    <Badge tone="success">Add</Badge>
  ) : (
    <Badge tone="danger">Remove</Badge>
  );
const statusBadge = (status: PermissionProposal['status']) =>
  status === 'pending' ? (
    <Badge tone="warning">Pending review</Badge>
  ) : status === 'approved' ? (
    <Badge tone="success">Approved</Badge>
  ) : (
    <Badge tone="neutral">Rejected</Badge>
  );
/** RL-2019: decided by the proposer alone (no one else could approve). */
const selfBadge = (c: PermissionProposal) =>
  c.selfApproved ? (
    <Badge tone="warning">
      {c.status === 'rejected' ? 'Self-rejected' : 'Self-approved'}
    </Badge>
  ) : null;

/**
 * "Permission matrix" (prototype permissions(): capability rows × role
 * columns, F / R / -) plus the two-person change workflow (RL-1035). The page
 * never edits grants: it files a proposal, and another super admin approves
 * or rejects it. Every rule is enforced by the API; buttons only mirror it.
 */
export function Permissions() {
  const me = useMe();
  const { hasPermission } = useSession();
  const matrix = usePermissionMatrix();
  const changes = usePermissionChanges();
  const canPropose = hasPermission('permissions:propose');
  const canApprove = hasPermission('permissions:approve');
  const [roleKey, setRoleKey] = useState('');
  const [draft, setDraft] = useState<ProposalDraft | null>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const roles = [...(matrix.data?.roles ?? [])].sort(
    (a, b) => ROLE_ORDER.indexOf(a.key) - ROLE_ORDER.indexOf(b.key),
  );
  const role = roles.find((r) => r.key === roleKey) ?? roles[0];
  const catalogue = (matrix.data?.permissions ?? []).filter(
    (p) => p.inCatalogue,
  );
  const areas = [...new Set(catalogue.map((p) => p.key.split(':')[0]))].sort();
  const level = (r: MatrixRole, area: string) => {
    const held = r.permissions.filter((p) => p.split(':')[0] === area);
    if (!held.length) return '-';
    return held.some((p) => !p.endsWith(':read')) ? 'F' : 'R';
  };
  const pending = (changes.data ?? []).filter((c) => c.status === 'pending');
  // RL-2019: the API says when you are the only one who can approve (and
  // only someone with permissions:approve can).
  const lone = canApprove && pending.some((c) => c.selfApprovable);
  const history = (changes.data ?? []).filter((c) => c.status !== 'pending');
  const pendingFor = (r: string, p: string) =>
    pending.find((c) => c.roleKey === r && c.permission === p);
  const roleName = (key: string) =>
    roles.find((r) => r.key === key)?.name ?? label(key);

  return (
    <PageContainer>
      <Card
        title="Permission matrix"
        flush
        actions={<span className={styles.sub}>F full · R read · - none</span>}
      >
        <DataTable
          caption="Permission matrix"
          rows={matrix.data ? areas : undefined}
          rowKey={(a) => a}
          loading={matrix.isLoading}
          error={matrix.error}
          onRetry={() => void matrix.refetch()}
          columns={[
            {
              key: 'cap',
              header: 'Capability',
              render: (a) => <strong>{areaName(a)}</strong>,
            },
            ...roles.map((r) => ({
              key: r.key,
              header: r.name.split(' / ')[0],
              align: 'center' as const,
              render: (a: string) => {
                const l = level(r, a);
                return l === '-' ? (
                  <span className={styles.sub}>-</span>
                ) : (
                  <Badge tone={l === 'F' ? 'success' : 'info'}>{l}</Badge>
                );
              },
            })),
          ]}
        />
      </Card>

      <Card
        title={role ? `${role.name} · permissions` : 'Role permissions'}
        flush
        actions={
          roles.length > 0 && (
            <Select
              aria-label="Role"
              className={local.inlineSelect}
              value={role?.key ?? ''}
              onChange={(e) => setRoleKey(e.target.value)}
              options={roles.map((r) => ({ value: r.key, label: r.name }))}
            />
          )
        }
      >
        {role?.protected && (
          <p className={local.infoNote} style={{ margin: '12px 20px 0' }}>
            {role.name} permissions come only from the catalogue and cannot be
            changed here.
          </p>
        )}
        <DataTable
          caption="Role permissions"
          rows={matrix.data && role ? catalogue : undefined}
          rowKey={(p) => p.key}
          loading={matrix.isLoading}
          error={matrix.error}
          onRetry={() => void matrix.refetch()}
          empty={<EmptyState title="No permissions in the catalogue" />}
          columns={[
            {
              key: 'p',
              header: 'Permission',
              // RL-1793 (#154): the description, not the code.
              render: (p) => (
                <span title={p.key}>{permissionWords(p.key, catalogue)}</span>
              ),
            },
            {
              key: 'h',
              header: 'Access',
              render: (p) =>
                role!.permissions.includes(p.key) ? (
                  <Badge tone="success">Granted</Badge>
                ) : (
                  <span className={styles.sub}>Not granted</span>
                ),
            },
            {
              key: 's',
              header: 'Source',
              render: (p) => {
                const held = role!.permissions.includes(p.key);
                if (role!.protected || p.protected)
                  return <Badge tone="neutral">Catalogue only</Badge>;
                return held === role!.baseline.includes(p.key) ? (
                  <span className={styles.sub}>Catalogue</span>
                ) : (
                  <Badge tone="brand">Approved change</Badge>
                );
              },
            },
            {
              key: 'a',
              header: <span className="visually-hidden">Actions</span>,
              align: 'right' as const,
              render: (p) => {
                const open = pendingFor(role!.key, p.key);
                if (open)
                  return (
                    <Badge tone="warning">
                      {open.change === 'grant' ? 'Add' : 'Removal'} pending
                    </Badge>
                  );
                if (!canPropose || role!.protected || p.protected) return null;
                const held = role!.permissions.includes(p.key);
                return (
                  <Button
                    size="sm"
                    onClick={() =>
                      setDraft({
                        role: role!,
                        permission: p.key,
                        change: held ? 'revoke' : 'grant',
                      })
                    }
                  >
                    {held ? 'Propose removal' : 'Propose add'}
                  </Button>
                );
              },
            },
          ]}
        />
      </Card>

      <Card
        title="Pending changes"
        flush
        actions={
          <Badge tone="warning">
            {lone ? 'You are the only approver' : 'Second approval required'}
          </Badge>
        }
      >
        <DataTable
          caption="Pending permission changes"
          rows={changes.data ? pending : undefined}
          rowKey={(c) => c.id}
          loading={changes.isLoading}
          error={changes.error}
          onRetry={() => void changes.refetch()}
          empty={
            <EmptyState
              title="No pending changes"
              body="Access changes only after another Super admin approves a proposal."
            />
          }
          columns={[
            { key: 'r', header: 'Role', render: (c) => roleName(c.roleKey) },
            {
              key: 'p',
              header: 'Permission',
              render: (c) => permissionWords(c.permission, catalogue),
            },
            {
              key: 'c',
              header: 'Change',
              render: (c) => changeBadge(c.change),
            },
            { key: 'why', header: 'Reason', render: (c) => c.reason },
            {
              key: 'by',
              header: 'Proposed by',
              render: (c) => (
                <>
                  {c.proposedBy ?? 'Unknown'}
                  <div className={styles.sub}>{when(c.proposedAt)}</div>
                </>
              ),
            },
            {
              key: 'a',
              header: <span className="visually-hidden">Actions</span>,
              align: 'right' as const,
              render: (c) => {
                // RL-2019: your own proposal only when nobody else can approve.
                const self = c.proposedById === me.id;
                return self && !(canApprove && c.selfApprovable) ? (
                  <span className={styles.sub}>
                    Waiting for another Super admin
                  </span>
                ) : canApprove ? (
                  <span className={local.acts}>
                    <Button
                      size="sm"
                      onClick={() =>
                        setDecision({ proposal: c, action: 'reject', self })
                      }
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        setDecision({ proposal: c, action: 'approve', self })
                      }
                    >
                      {self ? 'Approve yourself' : 'Approve'}
                    </Button>
                  </span>
                ) : (
                  <span className={styles.sub}>Waiting for review</span>
                );
              },
            },
          ]}
        />
      </Card>

      <Card title="Change history" flush>
        <DataTable
          caption="Permission change history"
          rows={changes.data ? history : undefined}
          rowKey={(c) => c.id}
          loading={changes.isLoading}
          error={changes.error}
          onRetry={() => void changes.refetch()}
          empty={
            <EmptyState
              title="No decided changes yet"
              body="Approved and rejected proposals appear here."
            />
          }
          columns={[
            { key: 'r', header: 'Role', render: (c) => roleName(c.roleKey) },
            {
              key: 'p',
              header: 'Permission',
              render: (c) => permissionWords(c.permission, catalogue),
            },
            {
              key: 'c',
              header: 'Change',
              render: (c) => changeBadge(c.change),
            },
            {
              key: 's',
              header: 'Status',
              render: (c) => (
                <span className={local.badges}>
                  {statusBadge(c.status)}
                  {selfBadge(c)}
                </span>
              ),
            },
            {
              key: 'by',
              header: 'Proposed by',
              render: (c) => (
                <>
                  {c.proposedBy ?? 'Unknown'}
                  <div className={styles.sub}>{c.reason}</div>
                </>
              ),
            },
            {
              key: 'rv',
              header: 'Reviewed by',
              render: (c) => (
                <>
                  {c.decidedBy ?? 'Unknown'}
                  <div className={styles.sub}>
                    {when(c.decidedAt)}
                    {c.decisionNote ? ` · ${c.decisionNote}` : ''}
                  </div>
                </>
              ),
            },
          ]}
        />
      </Card>

      {draft && <ProposeDialog draft={draft} onClose={() => setDraft(null)} />}
      {decision && (
        <DecideDialog
          decision={decision}
          roleName={roleName(decision.proposal.roleKey)}
          onClose={() => setDecision(null)}
        />
      )}
    </PageContainer>
  );
}

function ProposeDialog({
  draft,
  onClose,
}: {
  draft: ProposalDraft;
  onClose: () => void;
}) {
  const matrix = usePermissionMatrix();
  const toast = useToast();
  const fail = useFail();
  const { propose } = usePermissionChangeActions();
  const [reason, setReason] = useState('');
  const short = reason.trim().length < 10;
  const submit = () =>
    propose
      .mutateAsync({
        roleKey: draft.role.key,
        permission: draft.permission,
        change: draft.change,
        reason: reason.trim(),
      })
      .then(() => {
        toast.show({
          tone: 'success',
          title: 'Change proposed',
          body: 'Access is unchanged until another Super admin approves it.',
        });
        onClose();
      }, fail);
  return (
    <Dialog
      open
      onClose={onClose}
      title={draft.change === 'grant' ? 'Propose adding' : 'Propose removing'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={short}
            loading={propose.isPending}
            onClick={() => void submit()}
          >
            Send for approval
          </Button>
        </>
      }
    >
      <div className={local.form}>
        <dl className={local.kv}>
          <dt>Role</dt>
          <dd>{draft.role.name}</dd>
          <dt>Permission</dt>
          <dd>
            {permissionWords(draft.permission, matrix.data?.permissions)}
            {/* The exact key, for the second person's check. */}
            <div className={`${styles.sub} mono`}>{draft.permission}</div>
          </dd>
          <dt>Change</dt>
          <dd>{changeBadge(draft.change)}</dd>
        </dl>
        <p className={local.infoNote}>
          Nothing changes now. Another Super admin must approve this proposal.
          Only if no one else can approve permission changes may you approve it
          yourself, with a note; it is then marked self-approved.
        </p>
        <Field
          label="Reason"
          hint="At least 10 characters. Kept in the audit log."
        >
          <textarea
            className={local.textarea}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}

function DecideDialog({
  decision,
  roleName,
  onClose,
}: {
  decision: Decision;
  roleName: string;
  onClose: () => void;
}) {
  const matrix = usePermissionMatrix();
  const toast = useToast();
  const fail = useFail();
  const { approve, reject } = usePermissionChangeActions();
  const [note, setNote] = useState('');
  const { proposal: p, action, self } = decision;
  const approving = action === 'approve';
  const mutation = approving ? approve : reject;
  // RL-2019: deciding your own proposal needs a note of 10+ characters.
  const minNote = self ? 10 : approving ? 0 : 3;
  const blocked = note.trim().length < minNote;
  const submit = () =>
    (approving
      ? approve.mutateAsync({ id: p.id, note: note.trim() || undefined })
      : reject.mutateAsync({ id: p.id, note: note.trim() })
    ).then(() => {
      toast.show({
        tone: 'success',
        title: approving ? 'Change approved and applied' : 'Change rejected',
        body: approving
          ? `${roleName} access was updated.${self ? ' Marked self-approved.' : ''}`
          : 'Access is unchanged.',
      });
      onClose();
    }, fail);
  return (
    <Dialog
      open
      onClose={onClose}
      title={
        self
          ? approving
            ? 'Approve your own change'
            : 'Reject your own change'
          : approving
            ? 'Approve change'
            : 'Reject change'
      }
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={blocked}
            loading={mutation.isPending}
            onClick={() => void submit()}
          >
            {approving ? 'Approve and apply' : 'Reject'}
          </Button>
        </>
      }
    >
      <div className={local.form}>
        <dl className={local.kv}>
          <dt>Role</dt>
          <dd>{roleName}</dd>
          <dt>Permission</dt>
          <dd>
            {permissionWords(p.permission, matrix.data?.permissions)}
            {/* The exact key, for the second person's check. */}
            <div className={`${styles.sub} mono`}>{p.permission}</div>
          </dd>
          <dt>Change</dt>
          <dd>{changeBadge(p.change)}</dd>
          <dt>Proposed by</dt>
          <dd>
            {p.proposedBy ?? 'Unknown'} · {when(p.proposedAt)}
          </dd>
          <dt>Reason</dt>
          <dd>{p.reason}</dd>
        </dl>
        {self && (
          <p className={local.note} role="note">
            No one else can approve permission changes now, so you may decide
            your own proposal. Your note is kept in the audit log and the change
            is marked self-approved.
          </p>
        )}
        <p className={approving ? local.note : local.infoNote}>
          {approving
            ? 'Approving applies exactly this change at once. It is final and audited.'
            : 'Rejecting leaves access unchanged. It is final and audited.'}
        </p>
        <Field
          label={
            self
              ? 'Why you decide it yourself'
              : approving
                ? 'Note (optional)'
                : 'Why it is rejected'
          }
          hint={minNote ? `At least ${minNote} characters.` : undefined}
        >
          <textarea
            className={local.textarea}
            maxLength={300}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
